import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Clock, CheckCircle, Plane, Home, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { linguistiquePrograms } from '../../data/programs';
import { formatPrice } from '../../lib/utils';
import SEO from '../../components/SEO';
import CallbackWidget from '../../components/CallbackWidget';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function Linguistique() {
  return (
    <div data-testid="linguistique-page">
      <SEO
        title="Formation Linguistique & Sejours a l'Etranger - Anglais des Affaires"
        description="Centre de Formation Linguistique CEFAT : sejours linguistiques a Accra (Ghana), anglais des affaires, preparation TOEFL/IELTS. Immersion totale depuis Abidjan."
        keywords="sejour linguistique Abidjan, anglais des affaires Cote d'Ivoire, formation anglais Abidjan, TOEFL IELTS Abidjan, immersion anglais Accra Ghana, CEFAT linguistique"
        url="/linguistique"
      />
      {/* Hero */}
      <section className="relative bg-[#8B5CF6] py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-white text-[#8B5CF6] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Centre de Formation Linguistique
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                Maîtrisez <span className="text-[#FFD700]">l'Anglais</span>
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Séjours linguistiques à Accra, anglais des affaires et immersion totale 
                pour booster votre employabilité internationale.
              </p>
              <Link to="/preinscription?center=linguistique">
                <Button className="bg-white text-[#8B5CF6] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-bold shadow-lg">
                  Je me préinscris <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1620829813573-7c9e1877706f?w=600&h=500&fit=crop"
                alt="Formation linguistique"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Plane, title: 'Immersion totale', desc: 'Vivez en anglais à Accra' },
              { icon: Home, title: 'Hébergement inclus', desc: 'Logement tout confort' },
              { icon: Users, title: 'Petits groupes', desc: 'Apprentissage personnalisé' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-6 bg-[#F8FAFC] rounded-xl">
                <div className="w-12 h-12 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                  <item.icon className="text-[#8B5CF6]" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#003366]">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge bg-[#8B5CF6]/10 text-[#8B5CF6] mb-4">Programmes</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Nos Programmes Linguistiques
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {linguistiquePrograms.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg card-hover"
              >
                <div className="h-2 bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA]" />
                <div className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#8B5CF6]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="text-[#8B5CF6]" size={28} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-[#003366]">{program.name}</h3>
                      <p className="flex items-center gap-2 mt-2 text-gray-500">
                        <Clock size={16} /> {program.duration}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">{program.description}</p>

                  {program.includes && (
                    <div className="mb-6">
                      <p className="font-semibold text-[#003366] mb-3">Inclus dans le programme</p>
                      <div className="space-y-2">
                        {program.includes.map((item, i) => (
                          <p key={i} className="flex items-center gap-2 text-gray-600">
                            <CheckCircle size={16} className="text-green-600" /> {item}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {program.levels && (
                    <div className="mb-6">
                      <p className="font-semibold text-[#003366] mb-3">Niveaux disponibles</p>
                      <div className="flex flex-wrap gap-2">
                        {program.levels.map((level, i) => (
                          <span key={i} className="bg-[#F8FAFC] px-3 py-1 rounded-full text-sm text-gray-600">
                            {level}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
                    <p className="text-sm text-gray-500 mb-1">Coût du programme</p>
                    <p className="font-heading text-2xl font-bold text-[#003366]">{formatPrice(program.price)}</p>
                  </div>

                  <Link to={`/preinscription?center=linguistique&program=${program.id}`}>
                    <Button className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full">
                      Je m'inscris <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Callback Widget */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <CallbackWidget variant="inline" center="linguistique" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#8B5CF6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ouvrez-vous au monde avec l'anglais
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Immersion garantie à Accra, Ghana - Le hub anglophone de l'Afrique de l'Ouest.
          </p>
          <Link to="/preinscription?center=linguistique">
            <Button className="bg-white text-[#8B5CF6] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-bold shadow-lg">
              Je me préinscris maintenant <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
